import React, { forwardRef } from 'react';

const ResultsDisplay = forwardRef(({ results, company }, ref) => {
  const { parsed, date, sources } = results;

  return (
    <div ref={ref} className="space-y-8">
      {/* Header Block */}
      <div 
        className="rounded-lg p-8"
        style={{ backgroundColor: 'hsl(var(--gb-surface))' }}
      >
        <div className="text-xs tracking-[0.2em] uppercase mb-3" style={{ color: 'hsl(var(--gb-gold))' }}>
          GEOBULL / GEOBEAR
        </div>
        <h2 
          className="text-3xl sm:text-4xl font-semibold mb-2"
          style={{ fontFamily: 'var(--font-display)', color: 'hsl(var(--gb-navy))' }}
          data-testid="company-title"
        >
          {company}
        </h2>
        <p 
          className="text-lg italic"
          style={{ color: 'hsl(var(--gb-text-muted))' }}
          data-testid="issue-subtitle"
        >
          {parsed.issue}
        </p>

        {/* Meta Bar */}
        <div 
          className="mt-6 p-4 rounded-lg grid grid-cols-1 sm:grid-cols-3 gap-4"
          style={{ backgroundColor: 'hsl(var(--gb-navy))' }}
        >
          <div>
            <div className="text-xs tracking-wider uppercase" style={{ color: 'hsl(var(--gb-gold))' }}>Ticker</div>
            <div className="text-sm font-medium mt-1" style={{ color: 'hsl(var(--gb-primary-foreground))' }} data-testid="ticker-value">
              {parsed.ticker}
            </div>
          </div>
          <div>
            <div className="text-xs tracking-wider uppercase" style={{ color: 'hsl(var(--gb-gold))' }}>Issue</div>
            <div className="text-sm font-medium mt-1" style={{ color: 'hsl(var(--gb-primary-foreground))' }} data-testid="issue-value">
              {parsed.issue}
            </div>
          </div>
          <div>
            <div className="text-xs tracking-wider uppercase" style={{ color: 'hsl(var(--gb-gold))' }}>Date</div>
            <div className="text-sm font-medium mt-1" style={{ color: 'hsl(var(--gb-primary-foreground))' }} data-testid="date-value">
              {date}
            </div>
          </div>
        </div>
      </div>

      {/* Executive Summary Section */}
      <div data-testid="executive-summary-section">
        <div className="flex items-center gap-4 mb-4">
          <div className="h-px flex-1" style={{ backgroundColor: 'hsl(var(--gb-gold))' }} />
          <h3 className="text-sm font-semibold tracking-[0.18em] uppercase" style={{ color: 'hsl(var(--gb-text-muted))' }}>
            EXECUTIVE SUMMARY
          </h3>
          <div className="h-px flex-1" style={{ backgroundColor: 'hsl(var(--gb-gold))' }} />
        </div>
        <div 
          className="rounded-lg p-6 border-l-4 space-y-3"
          style={{ 
            backgroundColor: 'hsl(var(--gb-surface))',
            borderLeftColor: 'hsl(var(--gb-gold))'
          }}
        >
          {parsed.executive_summary && parsed.executive_summary.length > 0 && (
            <>
              <p 
                className="text-sm sm:text-base leading-7 font-medium"
                style={{ color: 'hsl(var(--gb-navy))' }}
              >
                {parsed.executive_summary[0]}
              </p>
              {parsed.executive_summary.length > 1 && (
                <p 
                  className="text-sm sm:text-base leading-7 font-medium"
                  style={{ color: 'hsl(var(--gb-navy))' }}
                >
                  {parsed.executive_summary[1]}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* The Issue Section */}
      <div data-testid="issue-section">
        <div className="flex items-center gap-4 mb-4">
          <div className="h-px flex-1" style={{ backgroundColor: 'hsl(var(--gb-gold))' }} />
          <h3 className="text-sm font-semibold tracking-[0.18em] uppercase" style={{ color: 'hsl(var(--gb-text-muted))' }}>
            THE ISSUE
          </h3>
          <div className="h-px flex-1" style={{ backgroundColor: 'hsl(var(--gb-gold))' }} />
        </div>
        <div 
          className="rounded-lg p-6 border-l-4"
          style={{ 
            backgroundColor: 'hsl(var(--gb-surface))',
            borderLeftColor: 'hsl(var(--gb-gold))'
          }}
        >
          <p 
            className="text-sm sm:text-base leading-7"
            style={{ color: 'hsl(var(--gb-navy))' }}
          >
            {parsed.the_issue}
          </p>
        </div>
      </div>

      {/* The Company Section */}
      <div data-testid="company-section">
        <div className="flex items-center gap-4 mb-4">
          <div className="h-px flex-1" style={{ backgroundColor: 'hsl(var(--gb-gold))' }} />
          <h3 className="text-sm font-semibold tracking-[0.18em] uppercase" style={{ color: 'hsl(var(--gb-text-muted))' }}>
            THE COMPANY
          </h3>
          <div className="h-px flex-1" style={{ backgroundColor: 'hsl(var(--gb-gold))' }} />
        </div>
        <div 
          className="rounded-lg p-6 border-l-4"
          style={{ 
            backgroundColor: 'hsl(var(--gb-surface))',
            borderLeftColor: 'hsl(var(--gb-gold))'
          }}
        >
          <p 
            className="text-sm sm:text-base leading-7"
            style={{ color: 'hsl(var(--gb-navy))' }}
          >
            {parsed.the_company}
          </p>
        </div>
      </div>

      {/* Geopolitical Context Section */}
      {parsed.strategy_context && (
        <div data-testid="geopolitical-context-section">
          <div className="flex items-center gap-4 mb-4">
            <div className="h-px flex-1" style={{ backgroundColor: 'hsl(var(--gb-gold))' }} />
            <h3 className="text-sm font-semibold tracking-[0.18em] uppercase" style={{ color: 'hsl(var(--gb-text-muted))' }}>
              GEOPOLITICAL CONTEXT
            </h3>
            <div className="h-px flex-1" style={{ backgroundColor: 'hsl(var(--gb-gold))' }} />
          </div>
          <div 
            className="rounded-lg p-6 border-l-4 space-y-4"
            style={{ 
              backgroundColor: 'hsl(var(--gb-surface))',
              borderLeftColor: 'hsl(var(--gb-rust))'
            }}
          >
            <div>
              <h4 className="text-xs font-semibold tracking-wider uppercase mb-2" style={{ color: 'hsl(var(--gb-rust))' }}>
                Rule architecture
              </h4>
              <p className="text-sm sm:text-base leading-7" style={{ color: 'hsl(var(--gb-navy))' }}>
                {parsed.strategy_context.rule_architecture}
              </p>
            </div>
            
            <div>
              <h4 className="text-xs font-semibold tracking-wider uppercase mb-2" style={{ color: 'hsl(var(--gb-rust))' }}>
                Leverage map
              </h4>
              <p className="text-sm sm:text-base leading-7" style={{ color: 'hsl(var(--gb-navy))' }}>
                {parsed.strategy_context.leverage_map}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Debate Section */}
      <div data-testid="debate-section">
        {/* Headers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <div 
            className="p-4 rounded-lg flex items-center justify-center gap-2"
            style={{ backgroundColor: 'hsl(var(--gb-green-dark))' }}
          >
            <span className="text-xl" aria-hidden="true">🐂</span>
            <span className="text-xl" style={{ color: 'hsl(var(--gb-green-accent))' }}>↑</span>
            <h3 className="text-base font-semibold tracking-wider" style={{ color: 'hsl(var(--gb-green-accent))' }}>
              GEOBULLS SAY
            </h3>
          </div>
          <div 
            className="p-4 rounded-lg flex items-center justify-center gap-2"
            style={{ backgroundColor: 'hsl(var(--gb-rust))' }}
          >
            <span className="text-xl" aria-hidden="true">🐻</span>
            <span className="text-xl" style={{ color: '#FF8B67' }}>↓</span>
            <h3 className="text-base font-semibold tracking-wider" style={{ color: '#FF8B67' }}>
              GEOBEARS SAY
            </h3>
          </div>
        </div>

        {/* Arguments Grid */}
        <div className="space-y-4">
          {[0, 1, 2, 3, 4].map((index) => (
            <div key={index} className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Bull Card */}
              <div 
                className="p-5 rounded-lg border-l-4"
                style={{
                  backgroundColor: 'hsl(var(--gb-bull-surface))',
                  borderLeftColor: 'hsl(var(--gb-bull-border))'
                }}
                data-testid={`bull-card-${index}`}
              >
                {parsed.bulls[index] && (
                  <>
                    <div className="text-xs font-semibold tracking-wider uppercase mb-2" style={{ color: 'hsl(var(--gb-green-dark))' }}>
                      {parsed.bulls[index].label}
                    </div>
                    <div className="text-sm font-bold mb-2" style={{ color: 'hsl(var(--gb-green-dark))' }}>
                      {parsed.bulls[index].title}
                    </div>
                    <p className="text-sm leading-6" style={{ color: 'hsl(var(--gb-navy))' }}>
                      {parsed.bulls[index].body}
                    </p>
                  </>
                )}
              </div>

              {/* Bear Card */}
              <div 
                className="p-5 rounded-lg border-l-4"
                style={{
                  backgroundColor: 'hsl(var(--gb-bear-surface))',
                  borderLeftColor: 'hsl(var(--gb-bear-border))'
                }}
                data-testid={`bear-card-${index}`}
              >
                {parsed.bears[index] && (
                  <>
                    <div className="text-xs font-semibold tracking-wider uppercase mb-2" style={{ color: 'hsl(var(--gb-rust))' }}>
                      {parsed.bears[index].label}
                    </div>
                    <div className="text-sm font-bold mb-2" style={{ color: 'hsl(var(--gb-rust))' }}>
                      {parsed.bears[index].title}
                    </div>
                    <p className="text-sm leading-6" style={{ color: 'hsl(var(--gb-rust))' }}>
                      {parsed.bears[index].body}
                    </p>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Watch Section */}
      <div data-testid="watch-section">
        <div className="flex items-center gap-4 mb-4">
          <div className="h-px flex-1" style={{ backgroundColor: 'hsl(var(--gb-gold))' }} />
          <h3 className="text-sm font-semibold tracking-[0.18em] uppercase" style={{ color: 'hsl(var(--gb-text-muted))' }}>
            WATCH
          </h3>
          <div className="h-px flex-1" style={{ backgroundColor: 'hsl(var(--gb-gold))' }} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {parsed.watch.map((item, index) => (
            <div 
              key={index}
              className="p-4 rounded-lg border-t-2"
              style={{
                backgroundColor: 'hsl(var(--gb-surface))',
                borderTopColor: 'hsl(var(--gb-gold))'
              }}
              data-testid={`watch-item-${index}`}
            >
              <div className="text-sm font-bold mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>
                {item.trigger}
              </div>
              <p className="text-sm" style={{ color: 'hsl(var(--gb-text-muted))' }}>
                {item.detail}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Sources Section (optional, collapsible) */}
      {sources && Object.keys(sources).length > 0 && (
        <details className="no-print">
          <summary 
            className="cursor-pointer text-sm font-medium p-4 rounded-lg"
            style={{ 
              backgroundColor: 'hsl(var(--gb-surface-2))',
              color: 'hsl(var(--gb-navy))'
            }}
          >
            View Sources Used
          </summary>
          <div className="mt-4 space-y-4">
            {Object.entries(sources).map(([category, items]) => (
              <div key={category}>
                <h4 className="text-sm font-semibold mb-2" style={{ color: 'hsl(var(--gb-navy))' }}>
                  {category}
                </h4>
                <ul className="space-y-2">
                  {items.map((item, index) => (
                    <li key={index} className="text-sm">
                      <a 
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                        style={{ color: 'hsl(var(--gb-text-muted))' }}
                      >
                        {item.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* AI disclosure — required on both rendered and exported report.
          Sits after the sources block so it's the very last item in the
          on-screen report and clearly framed as a footnote. */}
      <p
        className="mt-10 pt-6 text-xs text-center leading-relaxed border-t"
        style={{
          color: 'hsl(var(--gb-text-muted))',
          borderColor: 'hsl(var(--gb-border))',
        }}
        data-testid="ai-disclaimer"
      >
        Disclaimer: this report was produced using artificial intelligence and may contain errors.
      </p>
    </div>
  );
});

export default ResultsDisplay;
